/**
 * The current route -> a stable screen code, for resolving which feedback form to
 * show.
 *
 * The code is the nav collection's `id` (`errormaster`, `feedback`, `units`),
 * NOT the URL. Paths are unstable — they get reorganised, and one screen owns
 * several of them (`/units`, `/units/create`, `/units/:id/edit`) — whereas the
 * nav id is the app's own long-lived name for a screen and is already declared
 * once in NAV_TAXONOMY. So there is nothing new to keep in sync here: add a
 * screen to the menu and it becomes addressable by feedback for free.
 *
 * Matching is LONGEST-PREFIX, so `/units/123/edit` resolves to `units` rather
 * than failing, and a future `/unitsomething` cannot be captured by `/units`
 * because the boundary must be a `/` or the end of the string.
 */
import { NAV_TAXONOMY, DASHBOARD } from '../../components/layout/nav-model';

interface Entry { code: string; path: string }

/** Built once. Collections only — a tab is the same screen, not another one. */
const ENTRIES: Entry[] = (() => {
  const out: Entry[] = [{ code: DASHBOARD.id, path: DASHBOARD.route }];
  for (const group of NAV_TAXONOMY) {
    for (const c of group.collections) {
      // `?tab=` lives on the children; the collection route is the bare path.
      out.push({ code: c.id, path: c.route.split('?')[0] });
    }
  }
  // Longest first, so the most specific prefix wins on the first hit.
  return out.sort((a, b) => b.path.length - a.path.length);
})();

/**
 * The screen code for a pathname, or null when the route is not a menu screen
 * (login, a stray path). Callers treat null as "no feedback here".
 */
export function screenCodeForPath(pathname: string): string | null {
  const path = (pathname || '').replace(/\/+$/, '') || '/';
  for (const e of ENTRIES) {
    if (path === e.path || path.startsWith(`${e.path}/`)) return e.code;
  }
  return null;
}

/** Every screen code, for the componentCode picker on the definition form. */
export const SCREEN_CODES: { label: string; value: string }[] = [
  { label: `Dashboard (${DASHBOARD.id})`, value: DASHBOARD.id },
  ...NAV_TAXONOMY.flatMap((g) => g.collections.map((c) => ({
    label: `${c.label} (${c.id})`,
    value: c.id,
  }))),
].sort((a, b) => a.label.localeCompare(b.label));

export default screenCodeForPath;
